/**
 * Stripe context — loads Stripe.js once on app mount and provides the instance.
 * Consumers use useStripeContext() to get the Stripe object.
 * Never call loadStripe() manually — use this context instead.
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

interface StripeContextType {
  stripe: Stripe | null;
  loading: boolean;
  error: string | null;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

let stripePromise: Promise<Stripe | null> | null = null;

export function StripeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StripeContextType>({
    stripe: null,
    loading: !!STRIPE_PUBLISHABLE_KEY,
    error: STRIPE_PUBLISHABLE_KEY
      ? null
      : 'Stripe publishable key not configured. Set VITE_STRIPE_PUBLISHABLE_KEY in your .env file.',
  });

  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return;

    stripePromise ??= loadStripe(STRIPE_PUBLISHABLE_KEY);

    stripePromise
      .then((instance) => {
        setState({
          stripe: instance,
          loading: false,
          error: instance ? null : 'Failed to load Stripe. Please check your publishable key.',
        });
      })
      .catch(() => {
        setState({ stripe: null, loading: false, error: 'Failed to load Stripe. Please try again later.' });
      });
  }, []);

  return <StripeContext.Provider value={state}>{children}</StripeContext.Provider>;
}

export function useStripeContext() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return context;
}
